import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { api } from '@/api/client'
import { CheckCircle, XCircle, Loader2 } from 'lucide-react'

type State = 'loading' | 'confirming' | 'confirmed' | 'error'

export default function FreigabeConfirmPage() {
  const { token } = useParams<{ token: string }>()
  const [state, setState] = useState<State>('loading')
  const [errorMsg, setErrorMsg] = useState<string>('')
  const [info, setInfo] = useState<{ firma_name: string; studi_name: string } | null>(null)

  useEffect(() => {
    if (!token) {
      setState('error')
      setErrorMsg('Kein Freigabe-Token gefunden.')
      return
    }

    let cancelled = false

    async function run() {
      try {
        // 1. Token-Details laden
        const data = await api.freigabeGet(token!)
        if (cancelled) return
        setInfo({ firma_name: data.firma_name, studi_name: data.studi_name })
        setState('confirming')

        // 2. Freigabe bestätigen
        await api.freigabeConfirm(token!)
        if (cancelled) return
        setState('confirmed')
      } catch (err) {
        if (cancelled) return
        setState('error')
        setErrorMsg(err instanceof Error ? err.message : 'Unbekannter Fehler.')
      }
    }

    run()
    return () => { cancelled = true }
  }, [token])

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center">

        {/* Logo / Branding */}
        <div className="mb-6">
          <span className="text-2xl font-black text-fom-600 tracking-tight">MyFOM</span>
        </div>

        {state === 'loading' || state === 'confirming' ? (
          <div className="flex flex-col items-center gap-4">
            <Loader2 size={48} className="text-fom-500 animate-spin" />
            <p className="text-gray-600 text-sm">
              {state === 'loading' ? 'Token wird geprüft…' : 'Freigabe wird bestätigt…'}
            </p>
          </div>
        ) : state === 'confirmed' ? (
          <div className="flex flex-col items-center gap-4">
            <CheckCircle size={48} className="text-green-500" />
            <h1 className="text-xl font-bold text-gray-900">Freigabe bestätigt!</h1>
            {info && (
              <p className="text-sm text-gray-600">
                <span className="font-semibold">{info.firma_name}</span> hat nun Zugriff auf
                Ihre Daten als Studierender{' '}
                <span className="font-semibold">{info.studi_name}</span>.
              </p>
            )}
            <p className="text-xs text-gray-400 mt-2">
              Sie können dieses Fenster jetzt schließen.
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <XCircle size={48} className="text-red-500" />
            <h1 className="text-xl font-bold text-gray-900">Fehler</h1>
            <p className="text-sm text-gray-600">{errorMsg || 'Der Freigabe-Link ist ungültig oder abgelaufen.'}</p>
            <p className="text-xs text-gray-400 mt-2">
              Bitte wenden Sie sich an Ihre Hochschule oder fordern Sie erneut eine Freigabe an.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
